
  import {EmployeeStatus, EmploymentTypes, WageTypes} from "@/enums";
  import {computed, defineComponent, onMounted, reactive, toRefs, watch} from "vue";
  import {isAuthorized} from "@/mixin/mixins";
  import router from "@/router";
  import EmployeeService, {EmployeeListItem, EmployeeV3Filter} from "@/service/EmployeeService";
  import {DataOptions} from "vuetify";
  import FilterUtils from "@/FilterUtils";

  // The v3 API rejects non-positive numbers and wages with more than two decimal places, so such input is
  // ignored instead of being sent
  const toPositiveInteger = (value: string | null): number | null => {
    return FilterUtils.isNum(value) && Number(value) > 0 ? Number(value) : null;
  };

  const toWage = (value: string | null): number | null => {
    if (value == null || !/^\d+([.,]\d{1,2})?$/.test(value)) {
      return null;
    }
    const wage = Number(value.replace(",", "."));
    return wage > 0 ? wage : null;
  };

  // The table's column values are named as in the v1/v2 API, the v3 API calls the employee id employeeNumber
  const sortProperties: Record<string, string> = {
    employeeId: "employeeNumber"
  };

  export default defineComponent({
    name: "EmployeesTable",
    setup(props, context) {
      const initialState = {
        filterRowVisible: true,
        filters: {
          employeeId: null,
          firstName: null,
          lastName: null,
          wageType: null,
          wage: null,
          employmentType: null,
          active: true,
          operatorNumber: null
        },
        headers: [
          {
            text: "ID",
            align: "right",
            value: "employeeId",
            visible: true,
            selectable: true
          },
          {
            text: "Vorname",
            align: "left",
            value: "firstName",
            visible: true,
            selectable: true
          },
          {
            text: "Nachname",
            align: "left",
            value: "lastName",
            visible: true,
            selectable: true
          },
          {
            text: "Entlohnungsart",
            align: "left",
            value: "wageType",
            authorization: "Accounting_Overview_ViewWageAndCosts",
            enumTypes: WageTypes,
            sortable: false,
            visible: true,
            selectable: true
          },
          {
            text: "Stundenlohn / Gehalt",
            align: "right",
            value: "wage",
            authorization: "Accounting_Overview_ViewWageAndCosts",
            visible: true,
            selectable: true
          },
          {
            text: "Beschäftigungsart",
            align: "left",
            value: "employmentType",
            enumTypes: EmploymentTypes,
            sortable: false,
            visible: true,
            selectable: true
          },
          {
            text: "Status",
            align: "left",
            value: "active",
            authorization: "Employee_Read_Inactive",
            sortable: false,
            visible: true,
            selectable: true,
            enumTypes: [{code: true, name: "Active", localizedName: "Aktiv"}, {
              code: false,
              name: "Inactive",
              localizedName: "Inaktiv"
            }]
          },
          {
            text: "Vertragsenddatum",
            align: "right",
            value: "contractExpiryDate",
            visible: false,
            selectable: true,
            filter: false
          },
          {
            text: "Bedienernummer",
            align: "right",
            value: "operatorNumber",
            visible: false,
            selectable: true,
            filter: true
          },
          {
            text: "",
            align: "right",
            value: "settings",
            sortable: false,
            width: 40,
            visible: true,
            selectable: false
          }
        ],
        options: {
          itemsPerPage: 25
        } as DataOptions,
        loading: false,
        columnDialog: false,
        columns: [],
        items: [] as Array<EmployeeListItem>,
        serverItemsLength: -1,
        typeTimeout: 300
      };

      const state = reactive({...initialState});

      const footerProps = {
        "items-per-page-options": [25, 50, 100, 250]
      };

      const availableHeaders = computed(() => {
        return state.headers.filter(header => header.authorization === undefined || isAuthorized(header.authorization));
      });

      const visibleHeaders = computed(() => {
        return availableHeaders.value.filter(header => header.visible);
      });

      const columnVisibilityMap = computed(() => {
        // @ts-ignore
        return Object.assign(...availableHeaders.value.map(({value, visible}) => ({[value]: visible})));
      });

      const openEmployee = (item: EmployeeListItem) => {
        router.push({path: `/employees/${item.uuid}`});
      };

      const openColumnDialog = () => {
        state.columns = JSON.parse(JSON.stringify(availableHeaders.value));
        state.columnDialog = true;
      };

      const saveColumnDialog = (newColumns: any) => {
        state.headers = newColumns;
        state.columnDialog = false;
      };

      watch(() => state.options, (newValue, oldValue) => {
        load();
      }, {deep: true});

      watch(() => state.filters, (newValue, oldValue) => {
        clearTimeout(state.typeTimeout);

        state.typeTimeout = setTimeout(function () {
          load();
        }, 300);
      }, {deep: true});

      const load = () => {
        if (state.loading) {
          return;
        }

        state.loading = true;

        let sort = null;
        if (state.options.sortBy && state.options.sortBy.length === 1) {
          const sortProperty = sortProperties[state.options.sortBy[0]] ?? state.options.sortBy[0];
          sort = state.options.sortDesc[0] ? sortProperty + ",desc" : sortProperty;
        }

        let employeeStatus = null;
        if (state.filters.active != null) {
          employeeStatus = state.filters.active ? EmployeeStatus.ACTIVE : EmployeeStatus.INACTIVE;
        }

        let filter: EmployeeV3Filter = {
          employeeNumber: toPositiveInteger(state.filters.employeeId),
          firstName: state.filters.firstName == "" ? null : state.filters.firstName,
          lastName: state.filters.lastName == "" ? null : state.filters.lastName,
          employmentType: state.filters.employmentType,
          wage: toWage(state.filters.wage),
          wageType: state.filters.wageType,
          status: employeeStatus,
          operatorNumber: toPositiveInteger(state.filters.operatorNumber)
        };

        EmployeeService.findEmployeeListItemsV3(filter, state.options.itemsPerPage, state.options.page - 1, sort)
          .then(result => {
            state.items = result.content;
            state.serverItemsLength = result.page.totalElements;
          })
          .finally(() => state.loading = false);
      };

      onMounted(() => {
        load();
      });

      return {
        ...toRefs(state),
        footerProps,
        availableHeaders,
        visibleHeaders,
        columnVisibilityMap,
        openColumnDialog,
        saveColumnDialog,
        openEmployee
      };
    }
  });
